/**
 * 实时行为分析服务
 * 基于 @mediapipe/tasks-vision 进行面部表情和身体姿态分析
 *
 * 架构：
 * 1. 使用 MediaPipe FaceLandmarker 检测 478 个面部关键点
 * 2. 从关键点推导表情指标（微笑、眨眼、眉毛、头部姿态等）
 * 3. 使用 MediaPipe PoseLandmarker 检测 33 个身体关键点
 * 4. 从关键点推导姿态指标（前倾、手势、稳定性等）
 */

import {
  FaceLandmarker,
  PoseLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
  type PoseLandmarkerResult,
} from '@mediapipe/tasks-vision';
import type {
  FrameAnalysis,
  FaceMetrics,
  PostureMetrics,
  BehaviorSummary,
} from '../types';

// ─── 模型配置 ────────────────────────────────────────────

/** MediaPipe WASM 本地路径（public/models/ 目录） */
const MODEL_BASE = '/models';

/** 模型文件（本地 public/models/ 目录，Vite 以 / 为根目录服务） */
const FACE_LANDMARKER_MODEL = '/models/face_landmarker.task';
const POSE_LANDMARKER_MODEL = '/models/pose_landmarker_lite.task';

/** 分析间隔（帧数），每 N 帧分析一次以节省性能 */
const ANALYSIS_INTERVAL = 3;

/** 统计窗口大小（帧数），用于计算滑动平均 */
const STATS_WINDOW = 30; // 约 1 秒（30fps）

/** 眨眼检测：眼睑纵横比阈值，低于此值视为闭眼 */
const EAR_BLINK_THRESHOLD = 0.2;

/**
 * 面部关键点索引
 * MediaPipe FaceMesh 使用 478 个关键点
 */
const FACE = {
  LEFT_EYE_TOP: 159,
  LEFT_EYE_BOTTOM: 145,
  LEFT_EYE_LEFT: 33,
  LEFT_EYE_RIGHT: 133,
  RIGHT_EYE_TOP: 386,
  RIGHT_EYE_BOTTOM: 374,
  RIGHT_EYE_LEFT: 362,
  RIGHT_EYE_RIGHT: 263,
  NOSE_TIP: 1,
  LEFT_MOUTH_CORNER: 61,
  RIGHT_MOUTH_CORNER: 291,
  UPPER_LIP_TOP: 13,
  LOWER_LIP_BOTTOM: 14,
  LEFT_EYEBROW: 52,   // 左眉弓中心
  RIGHT_EYEBROW: 282, // 右眉弓中心
  LEFT_EAR: 234,
  RIGHT_EAR: 454,
  FOREHEAD: 10,
  CHIN: 152,
} as const;

/**
 * 姿态关键点索引
 * MediaPipe Pose 使用 33 个关键点
 */
const POSE = {
  NOSE: 0,
  LEFT_EYE: 2,
  RIGHT_EYE: 5,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
} as const;

// ─── 类型 ────────────────────────────────────────────────

interface Landmark {
  x: number;
  y: number;
  z: number;
}

export interface ModelState {
  faceLandmarker: FaceLandmarker | null;
  poseLandmarker: PoseLandmarker | null;
  isLoaded: boolean;
  isLoading: boolean;
}

// ─── 模型管理 ────────────────────────────────────────────

let modelState: ModelState = {
  faceLandmarker: null,
  poseLandmarker: null,
  isLoaded: false,
  isLoading: false,
};

let loadPromise: Promise<void> | null = null;

/**
 * 初始化 MediaPipe 模型（懒加载单例）
 * 允许多次调用，只有第一次会真正加载
 */
export async function initializeModels(): Promise<void> {
  if (modelState.isLoaded) return;
  if (modelState.isLoading && loadPromise) return loadPromise;

  modelState.isLoading = true;

  loadPromise = (async () => {
    try {
      const vision = await FilesetResolver.forVisionTasks(MODEL_BASE);

      const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: FACE_LANDMARKER_MODEL,
          delegate: 'GPU',
        },
        outputFaceBlendshapes: true,
        runningMode: 'VIDEO',
        numFaces: 1,
      });

      const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: POSE_LANDMARKER_MODEL,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
      });

      modelState.faceLandmarker = faceLandmarker;
      modelState.poseLandmarker = poseLandmarker;
      modelState.isLoaded = true;
    } catch (err) {
      modelState.faceLandmarker = null;
      modelState.poseLandmarker = null;
      modelState.isLoaded = false;
      console.error('[BehaviorAnalysis] 模型加载失败:', err);
      throw err;
    } finally {
      modelState.isLoading = false;
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/** 释放模型资源 */
export function disposeModels(): void {
  modelState.faceLandmarker?.close();
  modelState.poseLandmarker?.close();
  modelState.faceLandmarker = null;
  modelState.poseLandmarker = null;
  modelState.isLoaded = false;
  modelState.isLoading = false;
  loadPromise = null;
}

export function isModelsLoaded(): boolean {
  return modelState.isLoaded;
}

export function getModels(): Readonly<ModelState> {
  return modelState;
}

// ─── 几何工具函数 ────────────────────────────────────────

/** 计算两点间欧氏距离 */
function distance(a: Landmark, b: Landmark): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2);
}

/** 计算眼部纵横比（EAR），用于检测眨眼 */
function eyeAspectRatio(
  top: Landmark,
  bottom: Landmark,
  left: Landmark,
  right: Landmark,
): number {
  const vertical = distance(top, bottom);
  const horizontal = distance(left, right);
  return horizontal === 0 ? 1 : vertical / horizontal;
}

/** 计算嘴部纵横比，用于检测张嘴 */
function mouthAspectRatio(
  top: Landmark,
  bottom: Landmark,
  left: Landmark,
  right: Landmark,
): number {
  const vertical = distance(top, bottom);
  const horizontal = distance(left, right);
  return horizontal === 0 ? 0 : vertical / horizontal;
}

/** 从 landmarks 数组中按索引获取点 */
function lm(landmarks: Landmark[], idx: number): Landmark {
  return landmarks[idx];
}

/** 平滑插值，避免数值抖动 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** 限制数值范围 */
function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

// ─── 面部分析 ────────────────────────────────────────────

/** 从 FaceLandmarker 结果中提取面部指标 */
function extractFaceMetrics(
  result: FaceLandmarkerResult | null,
  timestamp: number,
  prevBlinkRate: number,
): FaceMetrics {
  const empty: FaceMetrics = {
    smileScore: 0,
    eyeContact: false,
    blinkRate: prevBlinkRate,
    browPosition: 'normal',
    browHeight: 0,
    mouthOpen: 0,
    headPose: { pitch: 0, yaw: 0, roll: 0 },
    faceDetected: false,
  };

  if (!result?.faceLandmarks?.length) return empty;

  const landmarks = result.faceLandmarks[0];

  // ── 微笑检测 ──
  // 使用嘴部纵横比和 blendshapes 综合判断
  let smileScore = 0;

  // 从 blendshapes 读取微笑分数（如果有）
  if (result.faceBlendshapes?.length) {
    const blendshapes = result.faceBlendshapes[0].categories;
    const mouthSmileLeft = blendshapes.find(
      (b) => b.categoryName === 'mouthSmileLeft',
    );
    const mouthSmileRight = blendshapes.find(
      (b) => b.categoryName === 'mouthSmileRight',
    );
    if (mouthSmileLeft && mouthSmileRight) {
      smileScore = (mouthSmileLeft.score + mouthSmileRight.score) / 2;
    }
  }

  // 备选：从关键点几何推断微笑
  if (smileScore === 0) {
    const mouthLeft = lm(landmarks, FACE.LEFT_MOUTH_CORNER);
    const mouthRight = lm(landmarks, FACE.RIGHT_MOUTH_CORNER);
    const upperLip = lm(landmarks, FACE.UPPER_LIP_TOP);
    const lowerLip = lm(landmarks, FACE.LOWER_LIP_BOTTOM);
    const mar = mouthAspectRatio(upperLip, lowerLip, mouthLeft, mouthRight);
    // 正常闭嘴时 MAR 约 0.1-0.2，微笑时嘴角上扬导致 MAR 略增
    smileScore = clamp((mar - 0.1) * 3, 0, 1);
  }

  // ── 眨眼检测 ──
  const leftEyeTop = lm(landmarks, FACE.LEFT_EYE_TOP);
  const leftEyeBottom = lm(landmarks, FACE.LEFT_EYE_BOTTOM);
  const leftEyeLeft = lm(landmarks, FACE.LEFT_EYE_LEFT);
  const leftEyeRight = lm(landmarks, FACE.LEFT_EYE_RIGHT);
  const rightEyeTop = lm(landmarks, FACE.RIGHT_EYE_TOP);
  const rightEyeBottom = lm(landmarks, FACE.RIGHT_EYE_BOTTOM);
  const rightEyeLeft = lm(landmarks, FACE.RIGHT_EYE_LEFT);
  const rightEyeRight = lm(landmarks, FACE.RIGHT_EYE_RIGHT);

  const leftEAR = eyeAspectRatio(
    leftEyeTop, leftEyeBottom, leftEyeLeft, leftEyeRight,
  );
  const rightEAR = eyeAspectRatio(
    rightEyeTop, rightEyeBottom, rightEyeLeft, rightEyeRight,
  );
  const avgEAR = (leftEAR + rightEAR) / 2;
  const isBlinking = avgEAR < EAR_BLINK_THRESHOLD;

  // blinkRate 由外部维护，这里仅返回是否正在眨眼

  // ── 眉毛状态 ──
  const leftBrow = lm(landmarks, FACE.LEFT_EYEBROW);
  const rightBrow = lm(landmarks, FACE.RIGHT_EYEBROW);
  const leftEyeCenter = {
    x: (leftEyeLeft.x + leftEyeRight.x) / 2,
    y: (leftEyeLeft.y + leftEyeRight.y) / 2,
    z: (leftEyeLeft.z + leftEyeRight.z) / 2,
  };
  const rightEyeCenter = {
    x: (rightEyeLeft.x + rightEyeRight.x) / 2,
    y: (rightEyeLeft.y + rightEyeRight.y) / 2,
    z: (rightEyeLeft.z + rightEyeRight.z) / 2,
  };
  const browToEyeL = leftBrow.y - leftEyeCenter.y;
  const browToEyeR = rightBrow.y - rightEyeCenter.y;
  const avgBrowHeight = (browToEyeL + browToEyeR) / 2;

  // ── 嘴巴张开（需要先在眉毛判断之前计算，用于惊讶识别） ──
  const upperLip = lm(landmarks, FACE.UPPER_LIP_TOP);
  const lowerLip = lm(landmarks, FACE.LOWER_LIP_BOTTOM);
  const mouthLeft = lm(landmarks, FACE.LEFT_MOUTH_CORNER);
  const mouthRight = lm(landmarks, FACE.RIGHT_MOUTH_CORNER);
  const mar = mouthAspectRatio(upperLip, lowerLip, mouthLeft, mouthRight);
  const mouthOpen = clamp((mar - 0.2) * 5, 0, 1);

  let browPosition: 'raised' | 'furrowed' | 'normal' = 'normal';
  // 惊讶：眉毛明显抬高 + 嘴巴张开（防止平静时眉毛自然偏高被误判）
  if (avgBrowHeight < -0.065 && mouthOpen > 0.15) {
    browPosition = 'raised';
  } else if (avgBrowHeight > -0.015) {
    browPosition = 'furrowed';
  }

  // ── 头部姿态（近似估算） ──
  // 使用鼻尖、左耳、右耳、额头、下巴估算
  const nose = lm(landmarks, FACE.NOSE_TIP);
  const leftEar = lm(landmarks, FACE.LEFT_EAR);
  const rightEar = lm(landmarks, FACE.RIGHT_EAR);
  const forehead = lm(landmarks, FACE.FOREHEAD);
  const chin = lm(landmarks, FACE.CHIN);

  // Yaw（左右转头）：鼻尖相对于左右耳连线的偏移
  const earMidX = (leftEar.x + rightEar.x) / 2;
  const yaw = (nose.x - earMidX) * 2;

  // Pitch（上下点头）：额头到下巴的垂直线与鼻尖偏移
  const faceHeight = distance(forehead, chin);
  const noseOffset = (() => {
    const midY = (forehead.y + chin.y) / 2;
    return nose.y - midY;
  })();
  const pitch = faceHeight > 0 ? noseOffset / faceHeight * 2 : 0;

  // Roll（歪头）：左右耳高度差
  const roll = Math.atan2(rightEar.y - leftEar.y, rightEar.x - leftEar.x);

  // ── 眼神接触 ──
  // 近似判断：面部居中、正对镜头时视为眼神接触
  const isCentered = Math.abs(nose.x - 0.5) < 0.15 && Math.abs(nose.y - 0.5) < 0.15;
  const isFacingForward = Math.abs(yaw) < 0.15 && Math.abs(pitch) < 0.15;
  const eyeContact = isCentered && isFacingForward;

  return {
    smileScore,
    eyeContact,
    blinkRate: prevBlinkRate,
    browPosition,
    browHeight: avgBrowHeight,
    mouthOpen,
    headPose: { pitch, yaw, roll },
    faceDetected: true,
  };
}

// ─── 姿态分析 ────────────────────────────────────────────

/** 从 PoseLandmarker 结果中提取姿态指标 */
function extractPostureMetrics(
  result: PoseLandmarkerResult | null,
  prevStability: number,
): PostureMetrics {
  const empty: PostureMetrics = {
    leanAngle: 0,
    armsCrossed: false,
    gestureActivity: 0,
    stability: prevStability,
    bodyDetected: false,
  };

  if (!result?.landmarks?.length) return empty;

  const landmarks = result.landmarks[0];

  const nose = landmarks[POSE.NOSE];
  const leftShoulder = landmarks[POSE.LEFT_SHOULDER];
  const rightShoulder = landmarks[POSE.RIGHT_SHOULDER];
  const leftElbow = landmarks[POSE.LEFT_ELBOW];
  const rightElbow = landmarks[POSE.RIGHT_ELBOW];
  const leftWrist = landmarks[POSE.LEFT_WRIST];
  const rightWrist = landmarks[POSE.RIGHT_WRIST];
  const leftHip = landmarks[POSE.LEFT_HIP];
  const rightHip = landmarks[POSE.RIGHT_HIP];

  // ── 前倾角度 ──
  // 计算鼻子相对于肩膀-髋部中线的偏移
  const shoulderMidX = (leftShoulder.x + rightShoulder.x) / 2;
  const shoulderMidY = (leftShoulder.y + rightShoulder.y) / 2;
  const hipMidX = (leftHip.x + rightHip.x) / 2;
  const hipMidY = (leftHip.y + rightHip.y) / 2;

  // 脊柱方向向量
  const spineDx = shoulderMidX - hipMidX;
  const spineDy = shoulderMidY - hipMidY;
  const spineLen = Math.sqrt(spineDx * spineDx + spineDy * spineDy);

  // 鼻子到脊柱中线的水平偏移（正值=前倾）
  const spineNormalX = -spineDy / (spineLen || 1);
  const noseOffset = (nose.x - shoulderMidX) * spineNormalX;
  const leanAngle = clamp(noseOffset * 90, -45, 45);

  // ── 抱臂检测 ──
  const wristToShoulderL = distance(leftWrist, leftShoulder);
  const wristToShoulderR = distance(rightWrist, rightShoulder);
  const elbowToHipL = distance(leftElbow, leftHip);
  const elbowToHipR = distance(rightElbow, rightHip);
  // 手腕靠近对侧肩膀或肘部靠近髋部 = 抱臂
  const crossL = distance(leftWrist, rightShoulder);
  const crossR = distance(rightWrist, leftShoulder);
  const armsCrossed =
    (crossL < 0.15 && crossR < 0.15) ||
    (elbowToHipL < 0.1 && elbowToHipR < 0.1);

  // ── 手势活跃度 ──
  // 手腕相对肩膀的运动幅度
  const gestureL = distance(leftWrist, leftShoulder);
  const gestureR = distance(rightWrist, rightShoulder);
  const gestureActivity = clamp((gestureL + gestureR) * 2, 0, 1);

  // ── 稳定性 ──
  // 基于鼻子位置的稳定性（越高越稳定）
  // 这里返回原始值，真正的稳定性需要跨帧计算
  const currentStability = clamp(
    lerp(prevStability, 1 - (noseOffset * noseOffset) * 10, 0.3),
    0,
    1,
  );

  return {
    leanAngle,
    armsCrossed,
    gestureActivity,
    stability: currentStability,
    bodyDetected: true,
  };
}

// ─── 综合指标计算 ────────────────────────────────────────

/** 从面部和姿态指标计算自信度、紧张度等综合评分 */
function computeBehaviorScore(
  face: FaceMetrics,
  posture: PostureMetrics,
): { confidence: number; nervousness: number; engagement: number } {
  // 自信度：微笑 + 眼神接触 + 姿态开放 + 不抱臂
  const smileFactor = face.smileScore * 30;
  const eyeContactFactor = face.eyeContact ? 25 : 0;
  const browFactor =
    face.browPosition === 'raised'
      ? -10
      : face.browPosition === 'furrowed'
        ? -5
        : 10;
  const armsFactor = posture.armsCrossed ? -15 : 10;
  const leanFactor = posture.leanAngle > 5 ? 15 : posture.leanAngle < -5 ? -5 : 5;
  const stabilityFactor = posture.stability * 10;

  const confidence = clamp(
    smileFactor + eyeContactFactor + browFactor + armsFactor + leanFactor + stabilityFactor,
    0,
    100,
  );

  // 紧张度：皱眉 + 频繁眨眼 + 抱臂 + 前倾过度 + 低头
  const browNervous =
    face.browPosition === 'furrowed'
      ? 25
      : face.browPosition === 'raised'
        ? 10
        : 0;
  const blinkFactor = Math.min(face.blinkRate / 30, 1) * 20;
  const armsNervous = posture.armsCrossed ? 20 : 0;
  const gestureNervous = posture.gestureActivity > 0.5 ? 10 : 0;
  const leanNervous = posture.leanAngle > 20 ? 15 : posture.leanAngle < -10 ? 20 : 0;

  const nervousness = clamp(
    browNervous + blinkFactor + armsNervous + gestureNervous + leanNervous,
    0,
    100,
  );

  // 投入度：眼神接触 + 面对镜头 + 适度前倾 + 稳定
  const engagement = clamp(
    (face.eyeContact ? 35 : 0) +
      (Math.abs(face.headPose.yaw) < 0.2 ? 20 : 5) +
      (posture.leanAngle > 0 && posture.leanAngle < 20 ? 20 : 5) +
      posture.stability * 20,
    0,
    100,
  );

  return { confidence, nervousness, engagement };
}

// ─── 帧分析器 ────────────────────────────────────────────

export interface FrameAnalyzerOptions {
  /** 分析间隔（帧数），默认 3 */
  interval?: number;
  /** 统计窗口大小，默认 30 */
  statsWindow?: number;
}

/**
 * 帧分析器
 * 管理逐帧分析的状态和滑动统计
 */
export class FrameAnalyzer {
  private frameCount = 0;
  private readonly interval: number;
  private readonly statsWindow: number;
  private earHistory: number[] = [];
  private prevFaceMetrics: FaceMetrics = {
    smileScore: 0,
    eyeContact: false,
    blinkRate: 0,
    browPosition: 'normal',
    browHeight: 0,
    mouthOpen: 0,
    headPose: { pitch: 0, yaw: 0, roll: 0 },
    faceDetected: false,
  };
  private prevPostureMetrics: PostureMetrics = {
    leanAngle: 0,
    armsCrossed: false,
    gestureActivity: 0,
    stability: 1,
    bodyDetected: false,
  };
  private blinkCount = 0;
  private lastEarState = false; // false=睁眼, true=闭眼
  private readonly startTime: number;

  constructor(options?: FrameAnalyzerOptions) {
    this.interval = options?.interval ?? ANALYSIS_INTERVAL;
    this.statsWindow = options?.statsWindow ?? STATS_WINDOW;
    this.startTime = performance.now();
  }

  /**
   * 分析一帧视频
   * @returns 分析结果，如果跳帧则返回 null
   */
  analyze(
    video: HTMLVideoElement,
    faceResult: FaceLandmarkerResult | null,
    poseResult: PoseLandmarkerResult | null,
  ): FrameAnalysis | null {
    this.frameCount++;
    if (this.frameCount % this.interval !== 0) return null;

    const timestamp = Date.now();

    // ── 面部分析 ──
    const face = extractFaceMetrics(faceResult, timestamp, this.prevFaceMetrics.blinkRate);

    // 计算眨眼频率
    if (face.faceDetected) {
      const landmarks = faceResult!.faceLandmarks![0];
      const leftEAR = eyeAspectRatio(
        lm(landmarks, FACE.LEFT_EYE_TOP),
        lm(landmarks, FACE.LEFT_EYE_BOTTOM),
        lm(landmarks, FACE.LEFT_EYE_LEFT),
        lm(landmarks, FACE.LEFT_EYE_RIGHT),
      );
      const rightEAR = eyeAspectRatio(
        lm(landmarks, FACE.RIGHT_EYE_TOP),
        lm(landmarks, FACE.RIGHT_EYE_BOTTOM),
        lm(landmarks, FACE.RIGHT_EYE_LEFT),
        lm(landmarks, FACE.RIGHT_EYE_RIGHT),
      );
      const avgEAR = (leftEAR + rightEAR) / 2;

      // 下降沿检测：睁→闭
      if (!this.lastEarState && avgEAR < EAR_BLINK_THRESHOLD) {
        this.blinkCount++;
        this.lastEarState = true;
      } else if (avgEAR >= EAR_BLINK_THRESHOLD) {
        this.lastEarState = false;
      }

      // 滑动窗口内的眨眼频率（次/分钟）
      this.earHistory.push(avgEAR);
      if (this.earHistory.length > this.statsWindow) {
        this.earHistory.shift();
      }

      const elapsedMinutes = (performance.now() - this.startTime) / 60000;
      face.blinkRate =
        elapsedMinutes > 0
          ? Math.round(this.blinkCount / elapsedMinutes)
          : 0;
      face.eyeContact = face.eyeContact;
    }

    // ── 姿态分析 ──
    const posture = extractPostureMetrics(
      poseResult,
      this.prevPostureMetrics.stability,
    );

    // ── 综合评分 ──
    const score = computeBehaviorScore(face, posture);

    this.prevFaceMetrics = { ...face };
    this.prevPostureMetrics = { ...posture };

    return {
      timestamp,
      face,
      posture,
      ...score,
    };
  }

  /** 重置分析器状态 */
  reset(): void {
    this.frameCount = 0;
    this.earHistory = [];
    this.blinkCount = 0;
    this.lastEarState = false;
    this.prevFaceMetrics = {
      smileScore: 0,
      eyeContact: false,
      blinkRate: 0,
      browPosition: 'normal',
      browHeight: 0,
      mouthOpen: 0,
      headPose: { pitch: 0, yaw: 0, roll: 0 },
      faceDetected: false,
    };
    this.prevPostureMetrics = {
      leanAngle: 0,
      armsCrossed: false,
      gestureActivity: 0,
      stability: 1,
      bodyDetected: false,
    };
  }
}

// ─── 摘要生成 ────────────────────────────────────────────

/**
 * 从分析时间线生成面试行为摘要
 */
export function generateSummary(
  timeline: FrameAnalysis[],
  durationSeconds: number,
): BehaviorSummary {
  if (!timeline.length) {
    return {
      timeline: [],
      averageConfidence: 0,
      averageNervousness: 0,
      averageEngagement: 0,
      eyeContactRate: 0,
      smileFrequency: 0,
      postureStats: {
        forwardLeanRatio: 0,
        armsCrossedRatio: 0,
        averageStability: 0,
      },
      gazeStats: {
        lookingAtCamera: 0,
        lookingAway: 0,
        lookingDown: 0,
      },
      duration: durationSeconds,
    };
  }

  const total = timeline.length;

  // 综合评分平均
  const avgConfidence =
    timeline.reduce((s, f) => s + f.confidence, 0) / total;
  const avgNervousness =
    timeline.reduce((s, f) => s + f.nervousness, 0) / total;
  const avgEngagement =
    timeline.reduce((s, f) => s + f.engagement, 0) / total;

  // 眼神接触率
  const eyeContactFrames = timeline.filter((f) => f.face.eyeContact).length;
  const eyeContactRate = eyeContactFrames / total;

  // 微笑频率（0~1，微笑帧占比）
  const smileFrames = timeline.filter((f) => f.face.smileScore > 0.3).length;
  const smileFrequency = durationSeconds > 0 ? smileFrames / total : 0;

  // 姿态统计
  const forwardLeanFrames = timeline.filter(
    (f) => f.posture.leanAngle > 5,
  ).length;
  const armsCrossedFrames = timeline.filter(
    (f) => f.posture.armsCrossed,
  ).length;
  const avgStability =
    timeline.reduce((s, f) => s + f.posture.stability, 0) / total;

  // 注视方向
  const lookingAtCamera = timeline.filter(
    (f) => f.face.eyeContact,
  ).length;
  const lookingDown = timeline.filter(
    (f) => f.face.headPose.pitch > 0.15,
  ).length;
  const lookingAway = total - lookingAtCamera - lookingDown;

  return {
    timeline,
    averageConfidence: Math.round(avgConfidence),
    averageNervousness: Math.round(avgNervousness),
    averageEngagement: Math.round(avgEngagement),
    eyeContactRate,
    smileFrequency: Math.round(smileFrequency * 10) / 10,
    postureStats: {
      forwardLeanRatio: forwardLeanFrames / total,
      armsCrossedRatio: armsCrossedFrames / total,
      averageStability: Math.round(avgStability * 100) / 100,
    },
    gazeStats: {
      lookingAtCamera,
      lookingAway: Math.max(0, lookingAway),
      lookingDown,
    },
    duration: durationSeconds,
  };
}
